import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageCircle, RefreshCw, ExternalLink, EyeOff, Eye, Tag, Save, Link2, Copy, Check } from "lucide-react";
import { adminApi, fmtMoney, ESTADOS } from "@/api";
import { toast } from "sonner";

const ETIQUETAS = ["", "Contactado", "Gestionado", "No pagó / Baja", "Pagado", "Seguimiento"];

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm text-slate-800 text-right ${mono ? "font-data" : "font-medium"}`}>{value || "-"}</span>
    </div>
  );
}

export default function OperacionDetalle() {
  const { id } = useParams();
  const [op, setOp] = useState(null);
  const [nota, setNota] = useState("");
  const [etiqueta, setEtiqueta] = useState("");
  const [savingG, setSavingG] = useState(false);

  const load = () => adminApi.get(`/crm/operaciones/${id}`).then(({ data }) => { setOp(data); setNota(data.nota || ""); setEtiqueta(data.etiqueta || ""); }).catch(() => toast.error("No se encontro la operacion"));
  useEffect(() => { load(); }, [id]);

  const guardarGestion = async () => {
    setSavingG(true);
    try {
      await adminApi.put(`/crm/operaciones/${id}/gestion`, { nota, etiqueta });
      toast.success("Gestión guardada");
      load();
    } catch { toast.error("No se pudo guardar la gestión"); } finally { setSavingG(false); }
  };

  const toggleOcultar = async () => {
    try {
      const { data } = await adminApi.post(`/crm/operaciones/${id}/ocultar`, { oculto: !op.oculto });
      toast.success(data.oculto ? "Operación ocultada" : "Operación visible de nuevo");
      load();
    } catch { toast.error("No se pudo cambiar la visibilidad"); }
  };

  const navigate = useNavigate();
  const [copiadoLink, setCopiadoLink] = useState(false);
  const [regenerando, setRegenerando] = useState(false);

  const togglePago = async () => {
    try {
      const { data } = await adminApi.post(`/crm/operaciones/${id}/pago-habilitado`, { habilitado: !op.pago_habilitado });
      toast.success(data.pago_habilitado ? "Pago habilitado para este cliente" : "Pago bloqueado para este cliente");
      load();
    } catch { toast.error("No se pudo cambiar el estado de pago"); }
  };

  const toggleBloqueo = async () => {
    try {
      const { data } = await adminApi.post(`/crm/operaciones/${id}/link-bloqueado`, { bloqueado: !op.link_bloqueado });
      toast.success(data.link_bloqueado ? "Link bloqueado" : "Link habilitado de nuevo");
      load();
    } catch { toast.error("No se pudo cambiar el link"); }
  };

  const regenerarLink = async () => {
    if (!window.confirm("¿Regenerar el link? El link anterior deja de funcionar.")) return;
    setRegenerando(true);
    try {
      const { data } = await adminApi.post(`/crm/operaciones/${id}/regenerar-link`);
      toast.success("Link regenerado: el anterior ya no funciona");
      navigate(`/atm-seguros/operaciones/${data.public_id}`, { replace: true });
    } catch { toast.error("No se pudo regenerar el link"); } finally { setRegenerando(false); }
  };

  const copiarLink = () => {
    navigator.clipboard?.writeText(op.recovery_url);
    setCopiadoLink(true);
    setTimeout(() => setCopiadoLink(false), 2000);
  };

  if (!op) return <p className="text-slate-500">Cargando...</p>;
  const est = ESTADOS[op.estado] || ESTADOS.cotizado;
  const tel = (op.titular?.tel_cel || "").replace(/\D/g, "");
  const wa = tel ? `https://wa.me/${tel.startsWith("54") ? tel : `549${tel}`}?text=${encodeURIComponent(`Hola ${op.titular?.nombre || ""}, te contactamos de Seguros Para Moto por tu cotizacion.`)}` : null;

  const sync = async () => {
    try {
      const { data } = await adminApi.post(`/crm/operaciones/${id}/sync`);
      toast.success(`Estado actualizado: ${(ESTADOS[data.estado] || {}).label || data.estado}`);
      load();
    } catch {
      toast.error("No se pudo sincronizar con ATM");
    }
  };

  return (
    <div data-testid="operacion-detalle">
      <Link to="/atm-seguros" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Volver al panel</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Operacion #{op.operacion}</h1>
          <span className={`inline-block mt-1 text-xs font-semibold px-2 py-1 rounded-full ${est.cls}`} data-testid="op-estado">{est.label}</span>
          {op.emision_error && (
            <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3 max-w-md" data-testid="op-emision-error">
              <p className="text-xs font-semibold text-red-700 mb-0.5">Error de emisión de ATM:</p>
              <p className="text-sm text-red-800">{Array.isArray(op.emision_error) ? op.emision_error.join(" · ") : String(op.emision_error)}</p>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {wa && <a data-testid="contact-whatsapp-button" href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-2 h-10 px-4 rounded-md bg-[#25D366] text-white text-sm font-semibold"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
          <button data-testid="sync-button" onClick={sync} className="flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 text-sm font-medium bg-white hover:bg-slate-50"><RefreshCw className="w-4 h-4" /> Sincronizar estado</button>
          <button data-testid="ocultar-button" onClick={toggleOcultar} className="flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 text-sm font-medium bg-white hover:bg-slate-50">
            {op.oculto ? <><Eye className="w-4 h-4" /> Mostrar</> : <><EyeOff className="w-4 h-4" /> Ocultar</>}
          </button>
        </div>
      </div>

      {op.oculto && <div className="mb-4 bg-slate-100 border border-slate-200 rounded-lg p-3 text-sm text-slate-600" data-testid="op-oculta-banner">Esta operación está <b>oculta</b> del listado principal. Sigue accesible por link directo y en el filtro "Incluir ocultas".</div>}

      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="op-gestion">
        <h2 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2"><Tag className="w-4 h-4 text-slate-500" /> Gestión</h2>
        <div className="grid sm:grid-cols-[220px_1fr] gap-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Etiqueta</label>
            <select data-testid="gestion-etiqueta" value={etiqueta} onChange={(e) => setEtiqueta(e.target.value)}
              className="w-full h-10 px-3 bg-white border border-slate-300 rounded-md text-sm focus:border-emerald-600 outline-none">
              {ETIQUETAS.map((e) => <option key={e} value={e}>{e || "Sin etiqueta"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Comentario / nota interna</label>
            <textarea data-testid="gestion-nota" value={nota} onChange={(e) => setNota(e.target.value)} rows={2} placeholder="Ej: Cliente dijo que paga el viernes..."
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm focus:border-emerald-600 outline-none resize-none" />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button data-testid="gestion-save" onClick={guardarGestion} disabled={savingG}
            className="flex items-center gap-2 h-10 px-5 rounded-[10px] bg-[#1C1C1E] hover:bg-zinc-800 disabled:bg-slate-300 text-white text-sm font-semibold">
            <Save className="w-4 h-4" /> Guardar gestión
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="op-link-pago">
        <h2 className="font-heading font-semibold text-slate-900 mb-1 flex items-center gap-2"><Link2 className="w-4 h-4 text-slate-500" /> Link de pago del cliente</h2>
        <p className="text-xs text-slate-500 mb-3">Con "Emisión 100% manual" activa (Config → Validación WhatsApp), nadie puede pagar hasta que habilites el pago de esta operación. Le enviás este link por WhatsApp y paga solo, sin carga manual en ATM.</p>
        {op.recovery_url ? (
          <div className="flex items-center gap-2 mb-4">
            <input readOnly data-testid="link-pago-input" value={op.recovery_url} className="flex-1 min-w-0 text-xs font-data bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700" />
            <button data-testid="copiar-link-pago" onClick={copiarLink} className="shrink-0 h-9 w-9 rounded-lg border border-slate-300 flex items-center justify-center text-slate-600" title="Copiar link">
              {copiadoLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        ) : <p className="text-xs text-slate-400 mb-4">Todavía no hay link: se genera cuando el cliente completa sus datos.</p>}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-3">
            <button type="button" data-testid="toggle-pago-habilitado" onClick={togglePago}
              className={`relative w-12 h-7 rounded-full transition shrink-0 ${op.pago_habilitado ? "bg-emerald-600" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${op.pago_habilitado ? "left-[22px]" : "left-0.5"}`} />
            </button>
            <div>
              <p className="text-sm font-semibold text-slate-800">Pago habilitado</p>
              <p className="text-xs text-slate-500" data-testid="pago-habilitado-estado">{op.pago_habilitado ? "Este cliente PUEDE pagar con su link" : "Este cliente NO puede pagar (modo manual)"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" data-testid="toggle-link-bloqueado" onClick={toggleBloqueo}
              className={`relative w-12 h-7 rounded-full transition shrink-0 ${op.link_bloqueado ? "bg-red-500" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${op.link_bloqueado ? "left-[22px]" : "left-0.5"}`} />
            </button>
            <div>
              <p className="text-sm font-semibold text-slate-800">Link bloqueado</p>
              <p className="text-xs text-slate-500" data-testid="link-bloqueado-estado">{op.link_bloqueado ? "El link está deshabilitado" : "El link funciona normal"}</p>
            </div>
          </div>
          <button data-testid="regenerar-link-button" onClick={regenerarLink} disabled={regenerando}
            className="flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 text-sm font-medium bg-white hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${regenerando ? "animate-spin" : ""}`} /> Regenerar link
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Cliente</h2>
          {op.titular ? (
            <>
              <Row label="Nombre" value={`${op.titular.apellido}, ${op.titular.nombre}`} />
              <Row label={op.titular.tipodoc} value={op.titular.nrodoc} mono />
              <Row label="Email" value={op.titular.mail} />
              <Row label="Celular" value={op.titular.tel_cel} mono />
              <Row label="Domicilio" value={`${op.titular.calle} ${op.titular.altura}${op.titular.piso ? ` P${op.titular.piso}` : ""}, ${op.titular.localidad} (${op.titular.cp})`} />
            </>
          ) : <p className="text-sm text-slate-400">Todavia no cargo sus datos (solo cotizo).</p>}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Moto</h2>
          <Row label="Vehiculo" value={`${op.vehiculo_cotizado?.marca_desc} ${op.vehiculo_cotizado?.modelo_desc} ${op.vehiculo_cotizado?.anio}`} />
          <Row label="Uso" value={op.vehiculo_cotizado?.tipo_uso === 2 ? "Comercial" : "Particular"} />
          <Row label="Zona" value={`${op.vehiculo_cotizado?.localidad_desc} (CP ${op.vehiculo_cotizado?.cp})`} />
          <Row label="Patente" value={op.vehiculo?.patente} mono />
          <Row label="Suma asegurada" value={op.vehiculo_cotizado?.suma ? fmtMoney(op.vehiculo_cotizado.suma) : "-"} />
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Cobertura elegida</h2>
          {op.cobertura_elegida ? (
            <>
              <Row label="Cobertura" value={op.cobertura_elegida.descripcion} />
              <Row label="Premio" value={fmtMoney(op.cobertura_elegida.premio)} />
              <Row label="Cuota" value={`${fmtMoney(op.cobertura_elegida.impcuotas)} x ${Number(op.cobertura_elegida.cuotas)}`} />
            </>
          ) : <p className="text-sm text-slate-400">Sin cobertura elegida.</p>}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-400 mb-2">Coberturas cotizadas ({op.coberturas?.length || 0})</p>
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {(op.coberturas || []).map((c, i) => (
                <div key={i} className="flex justify-between text-xs text-slate-500"><span>{c.descripcion} ({c.plan_cot})</span><span>{fmtMoney(c.premio)}</span></div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Seguimiento</h2>
          <Row label="Operacion" value={op.operacion} mono />
          <Row label="Propuesta" value={op.npropuesta} mono />
          <Row label="Solicitud" value={op.solicitud} mono />
          <Row label="Poliza" value={op.npoliza} mono />
          <Row label="Creada" value={(op.created_at || "").replace("T", " ").slice(0, 16)} mono />
          <Row label="Emitida" value={(op.fecha_emision || "").replace("T", " ").slice(0, 16) || "-"} mono />
          <Row label="Pago detectado" value={(op.fecha_pago_detectado || "").replace("T", " ").slice(0, 16) || "-"} mono />
          {op.polling_desc && <Row label="Estado en ATM" value={op.polling_desc} />}
          {op.consentimiento && (
            <div className="mt-3 pt-3 border-t border-slate-100" data-testid="op-consentimiento">
              <p className="text-xs font-semibold text-slate-500 mb-1">Consentimiento inspección Robo (v{op.consentimiento.version})</p>
              <p className="text-xs text-slate-600 mb-1.5">{op.consentimiento.checkbox_texto}</p>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Aceptado: {(op.consentimiento.aceptado_at || "").replace("T", " ").slice(0, 16)}</span>
                <span className="font-semibold text-amber-700">Inspección: {op.consentimiento.inspeccion_estado || "pendiente"}</span>
              </div>
            </div>
          )}
          {op.auditoria && (
            <div className="mt-3 pt-3 border-t border-slate-100" data-testid="op-auditoria">
              <p className="text-xs font-semibold text-slate-500 mb-1">Auditoría de datos (DDJJ)</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                <span>IP: <b className="font-data">{op.auditoria.ip || "-"}</b></span>
                <span>Dispositivo: <b>{op.auditoria.dispositivo || "-"}</b></span>
                <span className="text-slate-400">{(op.auditoria.fecha || "").replace("T", " ").slice(0, 16)}</span>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            {op.linkpago && <a data-testid="linkpago-link" href={op.linkpago} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-emerald-600 hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Link de pago</a>}
            {op.recovery_url && <a data-testid="recovery-url-link" href={op.recovery_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-indigo-600 hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Link para retomar el pago</a>}
            {op.inspeccion_url && <a data-testid="inspeccion-link" href={op.inspeccion_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-emerald-600 hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Inspeccion web</a>}
            {op.whatsapp_url && <a data-testid="whatsapp-url-link" href={op.whatsapp_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-green-600 hover:underline"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp del cliente</a>}
          </div>
        </div>
      </div>
    </div>
  );
}
